import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Container,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  School as SchoolIcon,
  Chat as ChatIcon,
  Dashboard as DashboardIcon,
  Shield as ShieldIcon
} from '@mui/icons-material';
import { adminColors, TOUCH_HIT_SX } from './theme/adminTheme';

const BORDER_COLOR = '#e4e4e7';

const features = [
  {
    icon: ChatIcon,
    title: 'AI tutoring chat',
    description: 'Students get instant, curriculum-aware help in a safe, guided chat.',
  },
  {
    icon: DashboardIcon,
    title: 'Teacher & school dashboards',
    description: 'Manage classes, schedules, and materials in one place.',
  },
  {
    icon: ShieldIcon,
    title: 'Safety monitoring',
    description: 'Every conversation is supervised — flagged messages reach admins immediately.',
    highlight: true,
  },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: adminColors.surface,
        '@media (prefers-reduced-motion: reduce)': {
          '*': {
            transitionDuration: '0.01ms !important',
            animationDuration: '0.01ms !important',
          },
        },
      }}
    >
      {/* Header */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'white', borderBottom: `1px solid ${BORDER_COLOR}` }}>
        <Toolbar sx={{ py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                bgcolor: adminColors.accent,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1.5
              }}
            >
              <SchoolIcon sx={{ color: 'white', fontSize: 20 }} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography sx={{ color: adminColors.ink, fontWeight: 700, fontSize: 17, lineHeight: 1.2, letterSpacing: '-0.01em' }}>
                LearnAndMasterAI
              </Typography>
              <Typography variant="caption" sx={{ color: adminColors.inkSoft, fontSize: '0.75rem', lineHeight: 1 }}>
                Everything you need to succeed
              </Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            onClick={() => navigate('/signin')}
            sx={{
              borderColor: BORDER_COLOR,
              color: adminColors.ink,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              '&:hover': {
                borderColor: adminColors.accent,
                color: adminColors.accent,
                bgcolor: adminColors.accentLight,
              },
            }}
          >
            Sign In
          </Button>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Container maxWidth="xl" sx={{ pt: { xs: 9, md: 12 }, pb: { xs: 7, md: 9 } }}>
        <Box sx={{ textAlign: 'center', maxWidth: '900px', mx: 'auto' }}>
          <Typography
            variant={isMobile ? "h3" : "h2"}
            component="h1"
            sx={{
              fontWeight: 700,
              mb: 3,
              color: adminColors.ink,
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' }
            }}
          >
            Learn and Master <Box component="span" sx={{ color: adminColors.accent }}>AI</Box>
          </Typography>

          <Typography
            variant="h6"
            sx={{
              mb: 5,
              color: adminColors.inkSoft,
              lineHeight: 1.7,
              fontSize: { xs: '1.1rem', sm: '1.25rem' },
              fontWeight: 400,
              maxWidth: '700px',
              mx: 'auto'
            }}
          >
            Join thousands of students and educators using our AI-powered, chat-based platform to get instant help with assignments and achieve academic excellence.
          </Typography>

          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
          }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/signin')}
              sx={{
                bgcolor: adminColors.accent,
                px: { xs: 6, sm: 8 },
                py: { xs: 2, sm: 2.25 },
                textTransform: 'none',
                fontSize: { xs: '1rem', sm: '1.125rem' },
                fontWeight: 600,
                borderRadius: '8px',
                boxShadow: '0 8px 25px -8px rgba(79, 70, 229, 0.45)',
                minWidth: '200px',
                transition: 'transform 200ms cubic-bezier(0.23, 1, 0.32, 1), box-shadow 200ms ease, background-color 150ms ease',
                '&:hover': {
                  bgcolor: adminColors.accentDark,
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 30px -8px rgba(79, 70, 229, 0.55)'
                },
                '&:active': {
                  transform: 'scale(0.98)'
                }
              }}
            >
              Get Started
            </Button>
          </Box>
        </Box>
      </Container>

      {/* Features Section */}
      <Box sx={{ bgcolor: 'white', py: { xs: 8, md: 10 }, borderTop: `1px solid ${BORDER_COLOR}`, borderBottom: `1px solid ${BORDER_COLOR}` }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 7 }}>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                mb: 2,
                fontWeight: 700,
                color: adminColors.ink,
                letterSpacing: '-0.015em',
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
              }}
            >
              Built for schools
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: adminColors.inkSoft,
                maxWidth: '600px',
                mx: 'auto',
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                lineHeight: 1.6,
                fontWeight: 400
              }}
            >
              Our comprehensive platform provides all the tools and features needed for modern education
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 3,
              maxWidth: '1100px',
              mx: 'auto'
            }}
          >
            {features.map(({ icon: Icon, title, description, highlight }) => (
              <Card
                key={title}
                elevation={0}
                sx={{
                  p: 1,
                  borderRadius: '12px',
                  border: `1px solid ${highlight ? adminColors.accentBorder : BORDER_COLOR}`,
                  bgcolor: highlight ? adminColors.accentLight : 'white',
                  boxShadow: 'none',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '10px',
                      bgcolor: highlight ? adminColors.accent : adminColors.surface,
                      border: highlight ? 'none' : `1px solid ${BORDER_COLOR}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2.5,
                    }}
                  >
                    <Icon sx={{ fontSize: 22, color: highlight ? 'white' : adminColors.ink }} />
                  </Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '1.05rem', color: adminColors.ink, mb: 1 }}>
                    {title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: adminColors.inkSoft, lineHeight: 1.6 }}>
                    {description}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{
        bgcolor: adminColors.accentDark,
        py: { xs: 8, md: 10 },
      }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h3"
              sx={{
                color: 'white',
                mb: 3,
                fontWeight: 700,
                letterSpacing: '-0.015em',
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
              }}
            >
              Ready to Transform Your Learning?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(255,255,255,0.85)',
                mb: 5,
                lineHeight: 1.6,
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                maxWidth: '500px',
                mx: 'auto'
              }}
            >
              Join our community and start your AI-powered educational journey today.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/signin')}
              sx={{
                bgcolor: 'white',
                color: adminColors.accentDark,
                px: 7,
                py: 2,
                textTransform: 'none',
                fontSize: '1.125rem',
                fontWeight: 700,
                borderRadius: '8px',
                boxShadow: '0 8px 25px -8px rgba(0, 0, 0, 0.35)',
                transition: 'transform 200ms cubic-bezier(0.23, 1, 0.32, 1), box-shadow 200ms ease',
                '&:hover': {
                  bgcolor: '#f5f5f5',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 30px -8px rgba(0, 0, 0, 0.4)'
                },
                '&:active': {
                  transform: 'scale(0.98)'
                }
              }}
            >
              Get Started
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: adminColors.ink, py: 6 }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3
            }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: adminColors.accent,
                  borderRadius: '9px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1.5
                }}
              >
                <SchoolIcon sx={{ color: 'white', fontSize: 20 }} />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  color: 'white',
                  fontWeight: 700
                }}
              >
                LearnAndMasterAI
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', columnGap: 3, rowGap: 1, mb: 3 }}>
              <Typography
                component="a"
                href="mailto:info@learnandmasterai.com"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'rgba(255,255,255,0.75)',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  textDecoration: 'none',
                  '&:hover': { color: 'white' },
                  '&:focus-visible': { outline: '2px solid #a5b4fc', outlineOffset: 3, borderRadius: '3px' },
                  ...TOUCH_HIT_SX,
                }}
              >
                Contact
              </Typography>
              <Typography
                component="a"
                href="/signin"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'rgba(255,255,255,0.75)',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  textDecoration: 'none',
                  '&:hover': { color: 'white' },
                  '&:focus-visible': { outline: '2px solid #a5b4fc', outlineOffset: 3, borderRadius: '3px' },
                  ...TOUCH_HIT_SX,
                }}
              >
                Request access
              </Typography>
              <Typography
                component="a"
                href="/privacy"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'rgba(255,255,255,0.75)',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  textDecoration: 'none',
                  '&:hover': { color: 'white' },
                  '&:focus-visible': { outline: '2px solid #a5b4fc', outlineOffset: 3, borderRadius: '3px' },
                  ...TOUCH_HIT_SX,
                }}
              >
                Privacy Policy
              </Typography>
              <Typography
                component="a"
                href="/terms"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'rgba(255,255,255,0.75)',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  textDecoration: 'none',
                  '&:hover': { color: 'white' },
                  '&:focus-visible': { outline: '2px solid #a5b4fc', outlineOffset: 3, borderRadius: '3px' },
                  ...TOUCH_HIT_SX,
                }}
              >
                Terms of Service
              </Typography>
            </Box>

            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255,255,255,0.5)',
                maxWidth: '400px',
                mx: 'auto'
              }}
            >
              © 2026 LearnAndMasterAI. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
